import { HTTP } from '@awesome-cordova-plugins/http';
import { Capacitor } from '@capacitor/core';

const DEFAULT_JSON_HEADERS = {
  'Content-type': 'application/json',
  Accept: 'application/json',
};

// Use Capacitor platform detection so native apps consistently use native HTTP.
const isNativePlatform = () => Capacitor.isNativePlatform();

// @TODO: consider refactoring to a singleton class which instantiates per wifi setup flow
//        main benefit here is that we can set the base IP address on each instance

/**
 * Helper function to assert response types from the native HTTP wrapper.
 *
 * @param url - The full URL to call, including the base domain (e.g. `https://google.com`)
 * @param headers - Any custom headers you might want to apply, defaulted to sensible JSON values.
 */
export async function get<T>(url: string, headers: Record<string, string> = DEFAULT_JSON_HEADERS): Promise<T> {
  /* istanbul ignore if -- @preserve */
  if (isNativePlatform()) {
    const response = await HTTP.get(url, null, headers);
    // Response might be undefined, which can't be parsed as JSON.
    return response.data ? (JSON.parse(response.data) as T) : (response.data as T);
  } else {
    const res = await fetch(url, { method: 'GET', headers });
    if (!res.ok) throw new Error(`GET failed: ${res.status}`);
    const text = await res.text();
    return text ? (JSON.parse(text) as T) : ({} as T);
  }
}

/**
 * GET helper that returns both parsed data and status code.
 * Useful for polling flows that need to handle intermediate HTTP statuses (e.g. 202).
 */
export async function getWithStatus<T>(
  url: string,
  headers: Record<string, string> = DEFAULT_JSON_HEADERS
): Promise<{ data: T; status: number }> {
  /* istanbul ignore if -- @preserve */
  if (isNativePlatform()) {
    try {
      const response = await HTTP.get(url, null, headers);
      // The charger may return a non-JSON body (e.g. plain-text "Error: ...")
      // even on a 2xx. Catch parse failures and return the raw string as data.
      let parsed: T;
      try {
        parsed = response.data ? (JSON.parse(response.data) as T) : (response.data as T);
      } catch {
        parsed = response.data as unknown as T;
      }
      return { data: parsed, status: response.status };
    } catch (error) {
      // Cordova HTTP throws for non-2xx responses. The thrown object carries `status` and `error`.
      // Return them as a normal response so polling callers (e.g. fetchWifiNetworks) can
      // inspect the status instead of receiving an uncaught exception.
      const cordovaError = error as { status?: number; error?: string };
      if (typeof cordovaError.status === 'number' && cordovaError.status > 0) {
        return {
          data: cordovaError.error as unknown as T,
          status: cordovaError.status,
        };
      }
      throw error;
    }
  } else {
    const res = await fetch(url, { method: 'GET', headers });
    if (!res.ok) throw new Error(`GET failed: ${res.status}`);
    const text = await res.text();
    return {
      data: text ? (JSON.parse(text) as T) : ({} as T),
      status: res.status,
    };
  }
}

/**
 * Helper function to assert response types from the native HTTP wrapper.
 *
 * @param url - The full URL to call, including the base domain (e.g. `https://google.com`)
 * @param body - The body to be sent as the payload of the HTTP POST request.
 * @param headers - Any custom headers you might want to apply, defaulted to sensible JSON values.
 */
export async function post<T>(
  url: string,
  body: object,
  headers: Record<string, string> = DEFAULT_JSON_HEADERS
): Promise<T> {
  /* istanbul ignore if -- @preserve */
  if (isNativePlatform()) {
    await HTTP.setDataSerializer('json');
    const response = await HTTP.post(url, body, headers);
    // Response might be undefined, which can't be parsed as JSON.
    return response.data ? (JSON.parse(response.data) as T) : (response.data as T);
  } else {
    const res = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error(`POST failed: ${res.status}`);
    const text = await res.text();
    return text ? (JSON.parse(text) as T) : ({} as T);
  }
}

/**
 * Helper function to assert response types from the native HTTP wrapper.
 *
 * @param url - The full URL to call, including the base domain (e.g. `https://google.com`)
 * @param body - The body to be sent as the payload of the HTTP PUT request.
 * @param headers - Any custom headers you might want to apply, defaulted to sensible JSON values.
 */

/* istanbul ignore next -- @preserve */
export async function put<T>(
  url: string,
  body: object,
  headers: Record<string, string> = DEFAULT_JSON_HEADERS
): Promise<T> {
  if (isNativePlatform()) {
    await HTTP.setDataSerializer('json');
    const response = await HTTP.put(url, body, headers);
    // Response might be undefined, which can't be parsed as JSON.
    return response.data ? (JSON.parse(response.data) as T) : (response.data as T);
  } else {
    const res = await fetch(url, {
      method: 'PUT',
      headers,
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error(`PUT failed: ${res.status}`);
    const text = await res.text();
    return text ? (JSON.parse(text) as T) : ({} as T);
  }
}

/**
 * Helper function to assert response types from the native HTTP wrapper.
 *
 * @param url - The full URL to call, including the base domain (e.g. `https://google.com`)
 * @param body - The body to be sent as the payload of the HTTP PATCH request.
 * @param headers - Any custom headers you might want to apply, defaulted to sensible JSON values.
 */

/* istanbul ignore next -- @preserve */
export async function patch<T>(
  url: string,
  body: object,
  headers: Record<string, string> = DEFAULT_JSON_HEADERS
): Promise<T> {
  if (isNativePlatform()) {
    await HTTP.setDataSerializer('json');
    const response = await HTTP.patch(url, body, headers);
    // Response might be undefined, which can't be parsed as JSON.
    return response.data ? (JSON.parse(response.data) as T) : (response.data as T);
  } else {
    const res = await fetch(url, {
      method: 'PATCH',
      headers,
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error(`PATCH failed: ${res.status}`);
    const text = await res.text();
    return text ? (JSON.parse(text) as T) : ({} as T);
  }
}

/**
 * Helper function to assert response types from the native HTTP wrapper.
 *
 * @param url - The full URL to call, including the base domain (e.g. `https://google.com`)
 * @param headers - Any custom headers you might want to apply, defaulted to sensible JSON values.
 */

/* istanbul ignore next -- @preserve */
export async function del<T>(url: string, headers: Record<string, string> = DEFAULT_JSON_HEADERS): Promise<T> {
  if (isNativePlatform()) {
    const response = await HTTP.delete(url, null, headers);
    // Response might be undefined, which can't be parsed as JSON.
    return response.data ? (JSON.parse(response.data) as T) : (response.data as T);
  } else {
    const res = await fetch(url, {
      method: 'DELETE',
      headers,
    });
    if (!res.ok) throw new Error(`DELETE failed: ${res.status}`);
    const text = await res.text();
    return text ? (JSON.parse(text) as T) : ({} as T);
  }
}
